import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, updateDoc, collection, getDocs, query, where, addDoc, deleteDoc } from 'firebase/firestore';
import {updateProfile} from 'firebase/auth'
import { db } from '../../configs/firebase';
import { EAuthData } from '../Provider/EAuthProvider';
import { 
  FaBuilding, FaGlobe, FaMapMarkerAlt, FaPhone, FaEnvelope, 
  FaLinkedin, FaTwitter, FaFacebook, FaInstagram, FaEdit, 
  FaSave, FaTimes, FaPlus, FaTrash, FaUsers, FaAward,
  FaImage, FaUpload, FaEye, FaCheckCircle, FaExclamationTriangle,
  FaUserTie, FaCalendar, FaStar, FaIndustry, FaDollarSign,
  FaSpinner
} from 'react-icons/fa';

const CompanyProfileManagement = () => {
  const { user } = EAuthData();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [editMode, setEditMode] = useState({
    basic: false,
    branding: false,
    team: false,
    culture: false
  });

  const [companyProfile, setCompanyProfile] = useState({
    basic: {
      companyName: '',
      description: '',
      industry: '',
      companySize: '',
      founded: '',
      headquarters: '',
      website: '',
      phone: '',
      email: '',
      linkedinUrl: '',
      twitterUrl: '',
      facebookUrl: '',
      instagramUrl: ''
    },
    branding: {
      logo: '',
      coverImage: '',
      primaryColor: '#f97316',
      secondaryColor: '#1f2937',
      tagline: '',
      mission: '',
      vision: '',
      values: []
    },
    team: [],
    culture: {
      workEnvironment: '',
      benefits: [],
      perks: [],
      learningOpportunities: '',
      careerGrowth: ''
    }
  });

  const [newTeamMember, setNewTeamMember] = useState({
    name: '',
    position: '',
    department: '',
    email: '',
    linkedinUrl: '',
    bio: '',
    photo: '',
    startDate: ''
  });

  const [newValue, setNewValue] = useState('');
  const [newBenefit, setNewBenefit] = useState('');
  const [newPerk, setNewPerk] = useState('');

  const industries = [
    'Technology', 'Healthcare', 'Finance', 'Education', 'Manufacturing',
    'Retail', 'Consulting', 'Media', 'Non-profit', 'Government',
    'Real Estate', 'Transportation', 'Energy', 'Telecommunications',
    'Hospitality', 'Agriculture', 'Other'
  ];

  const companySizes = [
    '1-10 employees', '11-50 employees', '51-200 employees', 
    '201-500 employees', '501-1000 employees', '1000+ employees'
  ];

  useEffect(() => {
    fetchCompanyProfile();
  }, [user]);

  const fetchCompanyProfile = async () => {
    if (!user?.userd?.uid) return;

    try {
      setLoading(true);
      
      // FIRST: Load data from employers collection to sync basic info
      const employerRef = doc(db, 'employers', user.userd.uid);
      const employerSnap = await getDoc(employerRef);
      
      let syncedBasicInfo = {};
      
      if (employerSnap.exists()) {
        const employerData = employerSnap.data();
        
        // Map employer collection fields to company profile basic info structure
        syncedBasicInfo = {
          companyName: employerData.companyName || '',
          description: employerData.description || '',
          industry: employerData.industry || '',
          companySize: employerData.companySize || '',
          founded: employerData.foundedYear || '',
          headquarters: [
            employerData.streetAddress,
            employerData.city, 
            employerData.state,
            employerData.postalCode,
            employerData.country
          ].filter(Boolean).join(', '),
          website: employerData.website || '',
          phone: employerData.phone || '',
          email: employerData.email || '',
          linkedinUrl: employerData.linkedinProfile || '',
          twitterUrl: '',
          facebookUrl: '',
          instagramUrl: ''
        };
      }
      
      // SECOND: Load additional profile data from companyProfiles collection (for other sections)
      const profileRef = doc(db, 'companyProfiles', user.userd.uid);
      const profileSnap = await getDoc(profileRef);
      
      if (profileSnap.exists()) {
        const profileData = profileSnap.data();
        
        // Merge synced basic info with existing profile data
        setCompanyProfile(prev => ({
          ...prev,
          basic: {
            ...prev.basic,
            ...syncedBasicInfo, // Employer collection data takes precedence for basic info
            // Keep any additional fields from companyProfiles that don't exist in employers
            ...profileData.basic
          },
          // Keep other sections from companyProfiles
          branding: profileData.branding || prev.branding,
          culture: profileData.culture || prev.culture
        }));
      } else {
        // If no companyProfiles document exists, just use the synced basic info
        setCompanyProfile(prev => ({
          ...prev,
          basic: {
            ...prev.basic,
            ...syncedBasicInfo
          }
        }));
      }

      // THIRD: Load team data
      const teamRef = collection(db, 'teamMembers');
      const teamQuery = query(teamRef, where('employerId', '==', user.userd.uid));
      const teamSnap = await getDocs(teamQuery);
      const teamData = teamSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      setCompanyProfile(prev => ({
        ...prev,
        team: teamData
      }));

    } catch (error) {
      console.error('Error fetching company profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async (section) => {
    setSaving(true);
    try {
      // FIRST: Save to companyProfiles collection (existing functionality)
      const profileRef = doc(db, 'companyProfiles', user.userd.uid);
      const updateData = {
        [section]: companyProfile[section],
        updatedAt: new Date().toISOString(),
        employerId: user.userd.uid
      };

      const profileSnap = await getDoc(profileRef);
      if (profileSnap.exists()) {
        await updateDoc(profileRef, updateData);
      } else {
        await setDoc(profileRef, {
          ...updateData,
          createdAt: new Date().toISOString()
        });
      }

      // SECOND: If saving basic info, also sync relevant fields back to employers collection
      if (section === 'basic') {
        const employerRef = doc(db, 'employers', user.userd.uid);
        const employerUpdateData = {
          companyName: companyProfile.basic.companyName,
          description: companyProfile.basic.description,
          industry: companyProfile.basic.industry,
          companySize: companyProfile.basic.companySize,
          foundedYear: companyProfile.basic.founded,
          website: companyProfile.basic.website,
          phone: companyProfile.basic.phone,
          email: companyProfile.basic.email,
          linkedinProfile: companyProfile.basic.linkedinUrl,
          updatedAt: new Date()
        };

        // Parse headquarters back to individual address fields if needed
        if (companyProfile.basic.headquarters) {
          const addressParts = companyProfile.basic.headquarters.split(', ');
          if (addressParts.length >= 1) employerUpdateData.streetAddress = addressParts[0];
          if (addressParts.length >= 2) employerUpdateData.city = addressParts[1];
          if (addressParts.length >= 3) employerUpdateData.state = addressParts[2];
          if (addressParts.length >= 4) employerUpdateData.postalCode = addressParts[3];
          if (addressParts.length >= 5) employerUpdateData.country = addressParts[4];
        }

        await updateDoc(employerRef, employerUpdateData);
        console.log('✅ Basic info synced to employers collection');

        try {
          if (user.userd && companyProfile.basic.companyName !== user.userd.displayName) {
            await updateProfile(user.userd, {
              displayName: companyProfile.basic.companyName
            });
            console.log('✅ Firebase Auth displayName updated');
          }
        } catch (profileError) {
          console.error('⚠️ Error updating Firebase Auth displayName:', profileError);
          // Don't fail the entire save operation if this fails
        }
      }

      setEditMode(prev => ({ ...prev, [section]: false }));
      alert('Profile updated successfully!');
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Error saving profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const addTeamMember = async () => {
    if (!newTeamMember.name || !newTeamMember.position) {
      alert('Please fill in at least name and position.');
      return;
    }

    try {
      const teamMemberData = {
        ...newTeamMember,
        employerId: user.userd.uid,
        createdAt: new Date().toISOString()
      };

      const docRef = await addDoc(collection(db, 'teamMembers'), teamMemberData);
      
      setCompanyProfile(prev => ({
        ...prev,
        team: [...prev.team, { id: docRef.id, ...teamMemberData }]
      }));

      setNewTeamMember({
        name: '', position: '', department: '', email: '', 
        linkedinUrl: '', bio: '', photo: '', startDate: ''
      });

      alert('Team member added successfully!');
    } catch (error) {
      console.error('Error adding team member:', error);
      alert('Error adding team member. Please try again.');
    }
  };

  const removeTeamMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to remove this team member?')) return;

    try {
      await deleteDoc(doc(db, 'teamMembers', memberId));
      setCompanyProfile(prev => ({
        ...prev,
        team: prev.team.filter(member => member.id !== memberId)
      }));
      alert('Team member removed successfully!');
    } catch (error) {
      console.error('Error removing team member:', error);
      alert('Error removing team member. Please try again.');
    }
  };

  const addValue = () => {
    if (!newValue.trim()) return;
    setCompanyProfile(prev => ({
      ...prev,
      branding: {
        ...prev.branding,
        values: [...prev.branding.values, newValue.trim()]
      }
    }));
    setNewValue('');
  };

  const removeValue = (index) => {
    setCompanyProfile(prev => ({
      ...prev,
      branding: {
        ...prev.branding,
        values: prev.branding.values.filter((_, i) => i !== index)
      }
    }));
  };

  const addBenefit = () => {
    if (!newBenefit.trim()) return;
    setCompanyProfile(prev => ({
      ...prev,
      culture: {
        ...prev.culture,
        benefits: [...prev.culture.benefits, newBenefit.trim()]
      }
    }));
    setNewBenefit('');
  };

  const removeBenefit = (index) => {
    setCompanyProfile(prev => ({
      ...prev,
      culture: {
        ...prev.culture,
        benefits: prev.culture.benefits.filter((_, i) => i !== index)
      }
    }));
  };

  const addPerk = () => {
    if (!newPerk.trim()) return;
    setCompanyProfile(prev => ({
      ...prev,
      culture: {
        ...prev.culture,
        perks: [...prev.culture.perks, newPerk.trim()]
      }
    }));
    setNewPerk('');
  };

  const removePerk = (index) => {
    setCompanyProfile(prev => ({
      ...prev,
      culture: {
        ...prev.culture,
        perks: prev.culture.perks.filter((_, i) => i !== index)
      }
    }));
  };

  const updateField = (section, field, value) => {
    setCompanyProfile(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const BasicInfoTab = () => (
    <div className="bg-white rounded-lg shadow-md border border-gray-200">
      <div className="p-6 border-b border-gray-200 flex justify-between items-center">
        <h3 className="text-lg font-bold text-gray-900">Basic Information</h3>
        <button
          onClick={() => editMode.basic ? saveProfile('basic') : setEditMode(prev => ({ ...prev, basic: true }))}
          disabled={saving}
          className={`flex items-center px-4 py-2 rounded-md font-medium transition-colors ${
            editMode.basic 
              ? 'bg-green-500 hover:bg-green-600 text-white' 
              : 'bg-orange-500 hover:bg-orange-600 text-white'
          }`}
        >
          {saving ? (
            <FaSpinner className="animate-spin mr-2" />
          ) : editMode.basic ? (
            <FaSave className="mr-2" />
          ) : (
            <FaEdit className="mr-2" />
          )}
          {saving ? 'Saving...' : editMode.basic ? 'Save Changes' : 'Edit'}
        </button>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Company Name *</label>
            {editMode.basic ? (
              <input
                type="text"
                value={companyProfile.basic.companyName}
                onChange={(e) => updateField('basic', 'companyName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="Enter company name"
              />
            ) : (
              <p className="py-2 text-gray-900">{companyProfile.basic.companyName || 'Not specified'}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Industry</label>
            {editMode.basic ? (
              <select
                value={companyProfile.basic.industry}
                onChange={(e) => updateField('basic', 'industry', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="">Select industry</option>
                {industries.map(industry => (
                  <option key={industry} value={industry}>{industry}</option>
                ))}
              </select>
            ) : (
              <p className="py-2 text-gray-900">{companyProfile.basic.industry || 'Not specified'}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Company Size</label>
            {editMode.basic ? (
              <select
                value={companyProfile.basic.companySize}
                onChange={(e) => updateField('basic', 'companySize', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="">Select company size</option>
                {companySizes.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            ) : (
              <p className="py-2 text-gray-900">{companyProfile.basic.companySize || 'Not specified'}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Founded</label>
            {editMode.basic ? (
              <input
                type="number"
                value={companyProfile.basic.founded}
                onChange={(e) => updateField('basic', 'founded', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="e.g., 2020"
                min="1800"
                max={new Date().getFullYear()}
              />
            ) : (
              <p className="py-2 text-gray-900">{companyProfile.basic.founded || 'Not specified'}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Headquarters</label>
            {editMode.basic ? (
              <input
                type="text"
                value={companyProfile.basic.headquarters}
                onChange={(e) => updateField('basic', 'headquarters', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="City, State/Country"
              />
            ) : (
              <p className="py-2 text-gray-900 flex items-center">
                <FaMapMarkerAlt className="mr-2 text-gray-500" />
                {companyProfile.basic.headquarters || 'Not specified'}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
            {editMode.basic ? (
              <input
                type="url"
                value={companyProfile.basic.website}
                onChange={(e) => updateField('basic', 'website', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="https://www.example.com"
              />
            ) : (
              <p className="py-2 text-gray-900 flex items-center">
                <FaGlobe className="mr-2 text-gray-500" />
                {companyProfile.basic.website ? (
                  <a href={companyProfile.basic.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    {companyProfile.basic.website}
                  </a>
                ) : (
                  'Not specified'
                )}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
            {editMode.basic ? (
              <input
                type="tel"
                value={companyProfile.basic.phone}
                onChange={(e) => updateField('basic', 'phone', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="(555) 123-4567"
              />
            ) : (
              <p className="py-2 text-gray-900 flex items-center">
                <FaPhone className="mr-2 text-gray-500" />
                {companyProfile.basic.phone || 'Not specified'}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Contact Email</label>
            {editMode.basic ? (
              <input
                type="email"
                value={companyProfile.basic.email}
                onChange={(e) => updateField('basic', 'email', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="contact@company.com"
              />
            ) : (
              <p className="py-2 text-gray-900 flex items-center">
                <FaEnvelope className="mr-2 text-gray-500" />
                {companyProfile.basic.email || 'Not specified'}
              </p>
            )}
          </div>
        </div>

        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Company Description</label>
          {editMode.basic ? (
            <textarea
              value={companyProfile.basic.description}
              onChange={(e) => updateField('basic', 'description', e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="Tell potential interns about your company, what you do, and what makes you unique..."
            />
          ) : (
            <p className="py-2 text-gray-900 bg-gray-50 rounded-md p-3">
              {companyProfile.basic.description || 'No description provided'}
            </p>
          )}
        </div>

        <div className="mt-6">
          <h4 className="text-md font-semibold text-gray-900 mb-4">Social Media</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">LinkedIn</label>
              {editMode.basic ? (
                <input
                  type="url"
                  value={companyProfile.basic.linkedinUrl}
                  onChange={(e) => updateField('basic', 'linkedinUrl', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="https://linkedin.com/company/..."
                />
              ) : (
                <p className="py-2 text-gray-900 flex items-center">
                  <FaLinkedin className="mr-2 text-blue-600" />
                  {companyProfile.basic.linkedinUrl ? (
                    <a href={companyProfile.basic.linkedinUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      LinkedIn Profile
                    </a>
                  ) : (
                    'Not specified'
                  )}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Twitter</label>
              {editMode.basic ? (
                <input
                  type="url"
                  value={companyProfile.basic.twitterUrl}
                  onChange={(e) => updateField('basic', 'twitterUrl', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="https://twitter.com/..."
                />
              ) : (
                <p className="py-2 text-gray-900 flex items-center">
                  <FaTwitter className="mr-2 text-blue-400" />
                  {companyProfile.basic.twitterUrl ? (
                    <a href={companyProfile.basic.twitterUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      Twitter Profile
                    </a>
                  ) : (
                    'Not specified'
                  )}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const BrandingTab = () => (
    <div className="bg-white rounded-lg shadow-md border border-gray-200">
      <div className="p-6 border-b border-gray-200 flex justify-between items-center">
        <h3 className="text-lg font-bold text-gray-900">Branding & Identity</h3>
        <button
          onClick={() => editMode.branding ? saveProfile('branding') : setEditMode(prev => ({ ...prev, branding: true }))}
          disabled={saving}
          className={`flex items-center px-4 py-2 rounded-md font-medium transition-colors ${
            editMode.branding 
              ? 'bg-green-500 hover:bg-green-600 text-white' 
              : 'bg-orange-500 hover:bg-orange-600 text-white'
          }`}
        >
          {saving ? (
            <FaSpinner className="animate-spin mr-2" />
          ) : editMode.branding ? (
            <FaSave className="mr-2" />
          ) : (
            <FaEdit className="mr-2" />
          )}
          {saving ? 'Saving...' : editMode.branding ? 'Save Changes' : 'Edit'}
        </button>
      </div>

      <div className="p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Company Logo URL</label>
            {editMode.branding ? (
              <div className="space-y-2">
                <input
                  type="url"
                  value={companyProfile.branding.logo}
                  onChange={(e) => updateField('branding', 'logo', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="https://example.com/logo.png"
                />
                <p className="text-xs text-gray-500">Recommended: 200x200px, PNG/JPG format</p>
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                {companyProfile.branding.logo ? (
                  <img 
                    src={companyProfile.branding.logo} 
                    alt="Company Logo" 
                    className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className={`w-16 h-16 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center ${companyProfile.branding.logo ? 'hidden' : 'flex'}`}>
                  <FaImage className="text-gray-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-900">Company Logo</p>
                  <p className="text-xs text-gray-500">{companyProfile.branding.logo ? 'Logo uploaded' : 'No logo uploaded'}</p>
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Primary Color</label>
            {editMode.branding ? (
              <div className="flex items-center space-x-3">
                <input
                  type="color"
                  value={companyProfile.branding.primaryColor}
                  onChange={(e) => updateField('branding', 'primaryColor', e.target.value)}
                  className="w-12 h-10 border border-gray-300 rounded-md cursor-pointer"
                />
                <input
                  type="text"
                  value={companyProfile.branding.primaryColor}
                  onChange={(e) => updateField('branding', 'primaryColor', e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="#f97316"
                />
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <div 
                  className="w-10 h-10 rounded-md border border-gray-300"
                  style={{ backgroundColor: companyProfile.branding.primaryColor }}
                />
                <p className="text-gray-900">{companyProfile.branding.primaryColor}</p>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Secondary Color</label>
            {editMode.branding ? (
              <div className="flex items-center space-x-3">
                <input
                  type="color"
                  value={companyProfile.branding.secondaryColor}
                  onChange={(e) => updateField('branding', 'secondaryColor', e.target.value)}
                  className="w-12 h-10 border border-gray-300 rounded-md cursor-pointer"
                />
                <input
                  type="text"
                  value={companyProfile.branding.secondaryColor}
                  onChange={(e) => updateField('branding', 'secondaryColor', e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="#1f2937"
                />
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <div 
                  className="w-10 h-10 rounded-md border border-gray-300"
                  style={{ backgroundColor: companyProfile.branding.secondaryColor }}
                />
                <p className="text-gray-900">{companyProfile.branding.secondaryColor}</p>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Cover Image URL</label>
            {editMode.branding ? (
              <div className="space-y-2">
                <input
                  type="url"
                  value={companyProfile.branding.coverImage}
                  onChange={(e) => updateField('branding', 'coverImage', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="https://example.com/cover.jpg"
                />
                <p className="text-xs text-gray-500">Recommended: 1200x400px, JPG/PNG format</p>
              </div>
            ) : (
              <div className="space-y-2">
                {companyProfile.branding.coverImage ? (
                  <img 
                    src={companyProfile.branding.coverImage} 
                    alt="Cover" 
                    className="w-full h-24 object-cover rounded-lg border border-gray-200"
                  />
                ) : (
                  <div className="w-full h-24 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center">
                    <FaImage className="text-gray-400" />
                  </div>
                )}
                <p className="text-xs text-gray-500">{companyProfile.branding.coverImage ? 'Cover image uploaded' : 'No cover image'}</p>
              </div>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Vision Statement</label>
          {editMode.branding ? (
            <textarea
              value={companyProfile.branding.vision}
              onChange={(e) => updateField('branding', 'vision', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="Where do you see your company in the future?"
            />
          ) : (
            <p className="py-2 text-gray-900 bg-gray-50 rounded-md p-3">
              {companyProfile.branding.vision || 'No vision statement provided'}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Company Values</label>
          {editMode.branding ? (
            <div className="space-y-3">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Enter a company value"
                  onKeyPress={(e) => e.key === 'Enter' && addValue()}
                />
                <button
                  onClick={addValue}
                  className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors"
                >
                  <FaPlus />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {companyProfile.branding.values.map((value, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm"
                  >
                    {value}
                    <button
                      onClick={() => removeValue(index)}
                      className="ml-2 text-orange-600 hover:text-orange-800"
                    >
                      <FaTimes className="text-xs" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <div className="py-2">
              {companyProfile.branding.values.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {companyProfile.branding.values.map((value, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm"
                    >
                      {value}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-gray-900 bg-gray-50 rounded-md p-3">No values specified</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const TeamTab = () => (
    <div className="bg-white rounded-lg shadow-md border border-gray-200">
      <div className="p-6 border-b border-gray-200 flex justify-between items-center">
        <h3 className="text-lg font-bold text-gray-900">Team Members</h3>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-500">{companyProfile.team.length} members</span>
        </div>
      </div>

      <div className="p-6">
        {/* Add New Team Member Form */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h4 className="text-md font-semibold text-gray-900 mb-4">Add Team Member</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="Full Name *"
              value={newTeamMember.name}
              onChange={(e) => setNewTeamMember(prev => ({ ...prev, name: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <input
              type="text"
              placeholder="Position *"
              value={newTeamMember.position}
              onChange={(e) => setNewTeamMember(prev => ({ ...prev, position: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <input
              type="text"
              placeholder="Department"
              value={newTeamMember.department}
              onChange={(e) => setNewTeamMember(prev => ({ ...prev, department: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <input
              type="email"
              placeholder="Email"
              value={newTeamMember.email}
              onChange={(e) => setNewTeamMember(prev => ({ ...prev, email: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <input
              type="url"
              placeholder="LinkedIn URL"
              value={newTeamMember.linkedinUrl}
              onChange={(e) => setNewTeamMember(prev => ({ ...prev, linkedinUrl: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <input
              type="date"
              placeholder="Start Date"
              value={newTeamMember.startDate}
              onChange={(e) => setNewTeamMember(prev => ({ ...prev, startDate: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div className="mt-4">
            <textarea
              placeholder="Bio (optional)"
              value={newTeamMember.bio}
              onChange={(e) => setNewTeamMember(prev => ({ ...prev, bio: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div className="mt-4 flex justify-end">
            <button
              onClick={addTeamMember}
              className="flex items-center px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors"
            >
              <FaPlus className="mr-2" />
              Add Team Member
            </button>
          </div>
        </div>

        {/* Team Members List */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {companyProfile.team.map((member) => (
            <div key={member.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                    <FaUserTie className="text-orange-600" />
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-900">{member.name}</h5>
                    <p className="text-sm text-gray-600">{member.position}</p>
                    {member.department && (
                      <p className="text-xs text-gray-500">{member.department}</p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => removeTeamMember(member.id)}
                  className="text-red-500 hover:text-red-700 transition-colors"
                >
                  <FaTrash className="text-sm" />
                </button>
              </div>

              {member.bio && (
                <p className="text-sm text-gray-700 mb-3">{member.bio}</p>
              )}

              <div className="space-y-1 text-xs text-gray-500">
                {member.email && (
                  <div className="flex items-center">
                    <FaEnvelope className="mr-2" />
                    <span>{member.email}</span>
                  </div>
                )}
                {member.startDate && (
                  <div className="flex items-center">
                    <FaCalendar className="mr-2" />
                    <span>Started: {new Date(member.startDate).toLocaleDateString()}</span>
                  </div>
                )}
                {member.linkedinUrl && (
                  <div className="flex items-center">
                    <FaLinkedin className="mr-2 text-blue-600" />
                    <a href={member.linkedinUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      LinkedIn
                    </a>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {companyProfile.team.length === 0 && (
          <div className="text-center py-8">
            <FaUsers className="mx-auto text-gray-400 text-4xl mb-4" />
            <p className="text-gray-500">No team members added yet. Add your first team member above!</p>
          </div>
        )}
      </div>
    </div>
  );

  const CultureTab = () => (
    <div className="bg-white rounded-lg shadow-md border border-gray-200">
      <div className="p-6 border-b border-gray-200 flex justify-between items-center">
        <h3 className="text-lg font-bold text-gray-900">Company Culture</h3>
        <button
          onClick={() => editMode.culture ? saveProfile('culture') : setEditMode(prev => ({ ...prev, culture: true }))}
          disabled={saving}
          className={`flex items-center px-4 py-2 rounded-md font-medium transition-colors ${
            editMode.culture 
              ? 'bg-green-500 hover:bg-green-600 text-white' 
              : 'bg-orange-500 hover:bg-orange-600 text-white'
          }`}
        >
          {saving ? (
            <FaSpinner className="animate-spin mr-2" />
          ) : editMode.culture ? (
            <FaSave className="mr-2" />
          ) : (
            <FaEdit className="mr-2" />
          )}
          {saving ? 'Saving...' : editMode.culture ? 'Save Changes' : 'Edit'}
        </button>
      </div>

      <div className="p-6 space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Work Environment</label>
          {editMode.culture ? (
            <textarea
              value={companyProfile.culture.workEnvironment}
              onChange={(e) => updateField('culture', 'workEnvironment', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="Describe your work environment, office culture, remote work policies, etc."
            />
          ) : (
            <p className="py-2 text-gray-900 bg-gray-50 rounded-md p-3">
              {companyProfile.culture.workEnvironment || 'No work environment description provided'}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Benefits</label>
          {editMode.culture ? (
            <div className="space-y-3">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newBenefit}
                  onChange={(e) => setNewBenefit(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Enter a benefit (e.g., Health insurance, 401k matching)"
                  onKeyPress={(e) => e.key === 'Enter' && addBenefit()}
                />
                <button
                  onClick={addBenefit}
                  className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors"
                >
                  <FaPlus />
                </button>
              </div>
              <div className="space-y-2">
                {companyProfile.culture.benefits.map((benefit, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2 bg-green-50 rounded-md"
                  >
                    <span className="text-green-800">{benefit}</span>
                    <button
                      onClick={() => removeBenefit(index)}
                      className="text-red-500 hover:text-red-700 transition-colors"
                    >
                      <FaTimes />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="py-2">
              {companyProfile.culture.benefits.length > 0 ? (
                <div className="space-y-2">
                  {companyProfile.culture.benefits.map((benefit, index) => (
                    <div key={index} className="flex items-center p-2 bg-green-50 rounded-md">
                      <FaCheckCircle className="text-green-600 mr-2" />
                      <span className="text-green-800">{benefit}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-900 bg-gray-50 rounded-md p-3">No benefits specified</p>
              )}
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Perks & Amenities</label>
          {editMode.culture ? (
            <div className="space-y-3">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newPerk}
                  onChange={(e) => setNewPerk(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Enter a perk (e.g., Free snacks, Game room, Flexible hours)"
                  onKeyPress={(e) => e.key === 'Enter' && addPerk()}
                />
                <button
                  onClick={addPerk}
                  className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors"
                >
                  <FaPlus />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {companyProfile.culture.perks.map((perk, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                  >
                    {perk}
                    <button
                      onClick={() => removePerk(index)}
                      className="ml-2 text-blue-600 hover:text-blue-800"
                    >
                      <FaTimes className="text-xs" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <div className="py-2">
              {companyProfile.culture.perks.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {companyProfile.culture.perks.map((perk, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                    >
                      <FaStar className="mr-1" />
                      {perk}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-gray-900 bg-gray-50 rounded-md p-3">No perks specified</p>
              )}
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Learning & Development Opportunities</label>
          {editMode.culture ? (
            <textarea
              value={companyProfile.culture.learningOpportunities}
              onChange={(e) => updateField('culture', 'learningOpportunities', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="Describe training programs, mentorship, conferences, skill development opportunities..."
            />
          ) : (
            <p className="py-2 text-gray-900 bg-gray-50 rounded-md p-3">
              {companyProfile.culture.learningOpportunities || 'No learning opportunities described'}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Career Growth</label>
          {editMode.culture ? (
            <textarea
              value={companyProfile.culture.careerGrowth}
              onChange={(e) => updateField('culture', 'careerGrowth', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="Describe career advancement opportunities, promotion paths, and professional development..."
            />
          ) : (
            <p className="py-2 text-gray-900 bg-gray-50 rounded-md p-3">
              {companyProfile.culture.careerGrowth || 'No career growth information provided'}
            </p>
          )}
        </div>
      </div>
    </div>
  );

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: FaBuilding },
    { id: 'branding', label: 'Branding', icon: FaImage },
    { id: 'team', label: 'Team', icon: FaUsers },
    { id: 'culture', label: 'Culture', icon: FaAward }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="animate-spin text-4xl text-orange-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading company profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Company Profile Management</h1>
          <p className="text-gray-600 mt-2">
            Create and manage your company profile to attract top intern talent
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                      activeTab === tab.id
                        ? 'border-orange-500 text-orange-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="text-lg" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === 'basic' && <BasicInfoTab />}
          {activeTab === 'branding' && <BrandingTab />}
          {activeTab === 'team' && <TeamTab />}
          {activeTab === 'culture' && <CultureTab />}
        </div>

        {/* Profile Completion Status */}
        <div className="mt-8 bg-white rounded-lg shadow-md border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Profile Completion</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Basic Information</span>
              <span className={`text-sm font-medium ${companyProfile.basic.companyName ? 'text-green-600' : 'text-red-600'}`}>
                {companyProfile.basic.companyName ? 'Complete' : 'Incomplete'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Branding</span>
              <span className={`text-sm font-medium ${companyProfile.branding.logo ? 'text-green-600' : 'text-yellow-600'}`}>
                {companyProfile.branding.logo ? 'Complete' : 'Optional'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Team Members</span>
              <span className={`text-sm font-medium ${companyProfile.team.length > 0 ? 'text-green-600' : 'text-yellow-600'}`}>
                {companyProfile.team.length > 0 ? `${companyProfile.team.length} added` : 'None added'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Company Culture</span>
              <span className={`text-sm font-medium ${companyProfile.culture.workEnvironment ? 'text-green-600' : 'text-yellow-600'}`}>
                {companyProfile.culture.workEnvironment ? 'Complete' : 'Optional'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyProfileManagement;